import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import ShareCertificatePreviewModal from "../components/investor/ShareCertificatePreviewModal";
import {
  enrichProjectsWithHoldingsProjectFields,
  fetchPortfolio,
  fetchPriceHistory,
  fetchRedemptionBalance,
  listProjects,
  unwrapListPayload,
} from "../services/investmentService";
import { fetchMe } from "../services/authService";
import { fetchWallet } from "../services/paymentService";
import InvestorPageHeader from "../components/investor/InvestorPageHeader";
import InvestorMetricGrid from "../components/investor/InvestorMetricGrid";
import InvestmentOpportunitiesSection from "../components/investor/InvestmentOpportunitiesSection";
import InvestorSectionHeading from "../components/investor/InvestorSectionHeading";
import { IconCopy } from "../components/investor/InvestorMetricIcons";
import InvestorPortfolioVisualization from "../components/investor/InvestorPortfolioVisualization";
import { useAuth } from "../store/AuthContext";
import {
  filterInvestorCatalogProjects,
  normalizeProjectStatus,
} from "../utils/projectViews";
import { holdingBuyValueTotal, portfolioHoldingValueTotal } from "../utils/investmentPricing";
import { buildPortfolioSummaryMetrics } from "../utils/investorPortfolioMetrics";
import { investorLifecyclePillClass } from "../utils/investorLifecyclePill";

function projectKey(id) {
  return id == null ? "" : String(id);
}

export default function InvestorDashboardPage() {
  const navigate = useNavigate();
  const { user, refreshUser, accessToken } = useAuth();
  const [projects, setProjects] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [priceByProject, setPriceByProject] = useState({});
  const [priceRefreshingId, setPriceRefreshingId] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [redemptionBalances, setRedemptionBalances] = useState([]);
  const [referralCopyMsg, setReferralCopyMsg] = useState("");
  const [certPreviewProject, setCertPreviewProject] = useState(null);
  const [dashboardLoadError, setDashboardLoadError] = useState("");
  const bdtFull = useMemo(
    () => new Intl.NumberFormat("en-BD", { style: "currency", currency: "BDT", maximumFractionDigits: 2 }),
    []
  );
  const bdtAmount = useMemo(
    () => new Intl.NumberFormat("en-BD", { maximumFractionDigits: 2, minimumFractionDigits: 0 }),
    []
  );
  const shareFmt = useMemo(
    () => new Intl.NumberFormat("en-BD", { maximumFractionDigits: 2, minimumFractionDigits: 0 }),
    []
  );
  const formatShare = (n) => shareFmt.format(Number(n || 0));
  const moneyValue = useCallback(
    (amount, sign = "") => ({
      type: "money",
      sign,
      amount: bdtAmount.format(amount),
    }),
    [bdtAmount]
  );

  // Merge latest profile (referralLink, accountNumber) — login payload / localStorage can predate new API fields.
  useEffect(() => {
    if (!accessToken) return undefined;
    let cancelled = false;
    fetchMe()
      .then((res) => {
        if (!cancelled) refreshUser(res.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [accessToken, refreshUser]);

  useEffect(() => {
    setDashboardLoadError("");
    Promise.allSettled([listProjects(), fetchPortfolio(), fetchWallet(), fetchRedemptionBalance()])
      .then(([projectsRes, portfolioRes, walletRes, balRes]) => {
        const holdings =
          portfolioRes.status === "fulfilled" ? unwrapListPayload(portfolioRes.value.data) : [];
        setPortfolio(holdings);
        if (projectsRes.status === "fulfilled") {
          setProjects(
            filterInvestorCatalogProjects(
              enrichProjectsWithHoldingsProjectFields(unwrapListPayload(projectsRes.value.data), holdings)
            )
          );
        } else {
          setProjects([]);
        }
        setWalletBalance(walletRes.status === "fulfilled" ? Number(walletRes.value?.data?.balance) : null);
        if (balRes.status === "fulfilled") {
          const bal = balRes.value?.data;
          const balanceRows = Array.isArray(bal?.balances) ? bal.balances : bal?.project_id ? [bal] : [];
          setRedemptionBalances(balanceRows);
        } else {
          setRedemptionBalances([]);
        }
        const failed = [projectsRes, portfolioRes, walletRes, balRes].filter((r) => r.status === "rejected");
        if (failed.length === 4) {
          setDashboardLoadError("Could not load dashboard data. Please refresh and try again.");
        } else if (failed.length > 0) {
          setDashboardLoadError("Some dashboard data failed to load. Try refreshing.");
        }
      });
  }, []);

  const holdingsWithShares = useMemo(
    () => (portfolio || []).filter((h) => h.shares_held > 0),
    [portfolio]
  );

  const openSharesCertificate = useCallback((project) => {
    if (!project?.id) return;
    const holding = (portfolio || []).find((h) => String(h.project?.id) === String(project.id));
    if (!holding || Number(holding.shares_held) <= 0) return;
    setCertPreviewProject(project);
  }, [portfolio]);

  const loadPriceHistoryForProject = useCallback(async (projectId) => {
    const key = projectKey(projectId);
    if (!key) return false;
    try {
      const { data } = await fetchPriceHistory(projectId);
      const rows = Array.isArray(data) ? data : [];
      setPriceByProject((prev) => ({ ...prev, [key]: rows }));
      return true;
    } catch {
      return false;
    }
  }, []);

  const reloadPortfolioAndProjects = useCallback(async () => {
    try {
      const [projectsRes, portfolioRes] = await Promise.all([listProjects(), fetchPortfolio()]);
      const holdings = unwrapListPayload(portfolioRes.data);
      setPortfolio(holdings);
      setProjects(
        filterInvestorCatalogProjects(
          enrichProjectsWithHoldingsProjectFields(unwrapListPayload(projectsRes.data), holdings)
        )
      );
      return true;
    } catch {
      return false;
    }
  }, []);

  const refreshPriceHistory = useCallback(
    async (projectId) => {
      const key = projectKey(projectId);
      if (!key) return;
      setPriceRefreshingId(key);
      try {
        const priceOk = await loadPriceHistoryForProject(projectId);
        await reloadPortfolioAndProjects();
        if (!priceOk) {
          throw new Error("Failed to load price history");
        }
      } finally {
        setPriceRefreshingId(null);
      }
    },
    [loadPriceHistoryForProject, reloadPortfolioAndProjects]
  );

  useEffect(() => {
    let cancelled = false;
    const loadPrices = async () => {
      await Promise.all(
        holdingsWithShares.map(async (h) => {
          const pid = h.project?.id;
          if (!pid || cancelled) return;
          await loadPriceHistoryForProject(pid);
        })
      );
    };
    if (holdingsWithShares.length) loadPrices();
    else setPriceByProject({});
    return () => {
      cancelled = true;
    };
  }, [holdingsWithShares, loadPriceHistoryForProject]);

  const portfolioSummary = useMemo(() => {
    let shares = 0;
    let estValue = 0;
    let buyValue = 0;
    for (const h of holdingsWithShares) {
      const n = Number(h.shares_held) || 0;
      const p = h.project;
      shares += n;
      buyValue += holdingBuyValueTotal(h);
      estValue += portfolioHoldingValueTotal(h, p);
    }
    return { positions: holdingsWithShares.length, shares, estValue, buyValue };
  }, [holdingsWithShares]);

  const dashboardMetrics = useMemo(
    () => [
      {
        key: "wallet",
        icon: "wallet",
        label: "Available Balance",
        value:
          walletBalance != null && !Number.isNaN(walletBalance) ? moneyValue(walletBalance) : "—",
        accent: "teal",
        featured: true,
        hint: "Ready to invest",
      },
    ],
    [walletBalance, moneyValue]
  );

  const portfolioMetrics = useMemo(
    () =>
      buildPortfolioSummaryMetrics({
        holdings: portfolio,
        redemptionBalances,
        moneyValue,
        formatShare,
      }),
    [portfolio, redemptionBalances, moneyValue, formatShare]
  );

  const lifecyclePillClass = investorLifecyclePillClass;
  /** Only projects this investor redeemed, once those listings are fully REDEEMED. */
  const redeemedProjects = useMemo(() => {
    const redeemedIds = new Set();
    for (const b of redemptionBalances || []) {
      if (Number(b?.shares_redeemed_total || 0) > 0 && b?.project_id != null) {
        redeemedIds.add(String(b.project_id));
      }
    }
    for (const h of portfolio || []) {
      if (Number(h?.shares_redeemed_total || 0) > 0) {
        const pid = h?.project_id ?? h?.project?.id;
        if (pid != null) redeemedIds.add(String(pid));
      }
    }
    if (redeemedIds.size === 0) return [];
    return projects.filter((p) => {
      const st = normalizeProjectStatus(p?.status);
      return st === "REDEEMED" && redeemedIds.has(String(p.id));
    });
  }, [projects, portfolio, redemptionBalances]);

  const referralSignupLink = useMemo(() => {
    if (user?.referralLink) return user.referralLink;
    const acct = user?.accountNumber || user?.account_number;
    if (!acct) return "";
    const envBase = (process.env.REACT_APP_REFERRAL_PUBLIC_DOMAIN || "").replace(/\/$/, "");
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const base = envBase || origin;
    if (!base) return "";
    const raw = String(acct).trim();
    if (raw === "00") return "";
    const padded = /^\d+$/.test(raw) ? String(parseInt(raw, 10)).padStart(2, "0") : raw;
    return `${base}/campaign-${padded}`;
  }, [user?.referralLink, user?.accountNumber, user?.account_number]);

  const copyReferralLink = async () => {
    if (!referralSignupLink) return;
    try {
      await navigator.clipboard.writeText(referralSignupLink);
      setReferralCopyMsg("Copied.");
      setTimeout(() => setReferralCopyMsg(""), 2000);
    } catch {
      setReferralCopyMsg("Could not copy.");
      setTimeout(() => setReferralCopyMsg(""), 1500);
    }
  };

  const kycBadge = user?.kyc_status || "NOT_SUBMITTED";
  const welcomeName = String(user?.full_name || "").trim();
  const welcomeTitle = welcomeName ? `Welcome, ${welcomeName}!` : "Welcome!";

  return (
    <div className="page investor-premium investor-panel">
      <InvestorPageHeader
        title={welcomeTitle}
        subtitle="Your portfolio, wallet, and opportunities at a glance."
      />
      {dashboardLoadError ? <p className="msg msg-error">{dashboardLoadError}</p> : null}
      <InvestorMetricGrid metrics={dashboardMetrics} />

      {kycBadge !== "APPROVED" && (
        <div className="investor-alert investor-alert--warning">
          <InvestorSectionHeading icon="kyc" tag="span" className="investor-alert__title">
            KYC required to buy shares
          </InvestorSectionHeading>
          <p className="muted" style={{ margin: "6px 0 0" }}>
            Approved identity verification is required before buying project shares (and for BDT withdrawals). You can still deposit to your wallet.
          </p>
          <p className="muted" style={{ margin: "6px 0 0" }}>
            Status:{" "}
            <span
              className={`badge badge-${kycBadge === "REJECTED" ? "REJECTED" : "PENDING"}`}
            >
              {kycBadge}
            </span>
          </p>
          <Link to="/kyc">KYC →</Link>
        </div>
      )}

      {(user?.accountNumber || user?.account_number) && (
        <div className="card investor-referral-card">
          <InvestorSectionHeading icon="referral" className="investor-referral-card__title">
            Campaign Link
          </InvestorSectionHeading>
          {referralSignupLink ? (
            <div className="investor-referral-card__row">
              <code className="investor-referral-card__url">{referralSignupLink}</code>
              <button
                type="button"
                className={[
                  "btn-secondary",
                  "investor-referral-card__copy",
                  referralCopyMsg === "Copied." ? "investor-referral-card__copy--done" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={copyReferralLink}
                aria-label={referralCopyMsg === "Copied." ? "Campaign link copied" : "Copy campaign link"}
                title={referralCopyMsg === "Copied." ? "Copied" : "Copy campaign link"}
                aria-live="polite"
              >
                <span className="investor-referral-card__copy-icon" aria-hidden>
                  <IconCopy />
                </span>
              </button>
            </div>
          ) : (
            <p className="muted investor-referral-card__empty">
              Your shareable link will show here after the public site URL is configured for referrals.
            </p>
          )}
        </div>
      )}

      <div className="kpis" style={{ display: "none" }} aria-hidden>
        <div className="kpi">
          <div className="kpi-label">Launched Projects</div>
          <div className="kpi-value">{projects.length}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Portfolio positions</div>
          <div className="kpi-value">{portfolioSummary.positions}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Shares held (total)</div>
          <div className="kpi-value">{portfolioSummary.shares}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Cost basis (BDT)</div>
          <div className="kpi-value">{portfolioSummary.buyValue.toFixed(0)}</div>
        </div>
        <div className="kpi kpi--nav-focus">
          <div className="kpi-label">Est. holdings value</div>
          <div className="kpi-value">{portfolioSummary.estValue.toFixed(0)}</div>
        </div>
      </div>

      <section className="investor-section">
        <div className="investor-section__head">
          <div>
            <InvestorSectionHeading icon="portfolio">My portfolio</InvestorSectionHeading>
            <p className="investor-section__desc investor-section__desc--single-line-desktop">
              Cost basis, live NAV, and unrealised value. Expand a project for price history and redemption availability.
            </p>
          </div>
          <button type="button" className="btn-secondary" onClick={() => navigate("/investments")}>
            Buy shares
          </button>
        </div>
        <InvestorMetricGrid metrics={portfolioMetrics} />
        <InvestorPortfolioVisualization
          holdings={holdingsWithShares}
          redemptionBalances={redemptionBalances}
          formatShare={formatShare}
          formatMoney={(n) => bdtFull.format(n)}
          lifecyclePillClass={lifecyclePillClass}
          priceByProject={priceByProject}
          priceRefreshingId={priceRefreshingId}
          onRefreshPrice={refreshPriceHistory}
          onSharesCertificate={openSharesCertificate}
        />
      </section>

      <InvestmentOpportunitiesSection redeemedProjects={redeemedProjects} />

      {certPreviewProject ? (
        <ShareCertificatePreviewModal project={certPreviewProject} onClose={() => setCertPreviewProject(null)} />
      ) : null}
    </div>
  );
}
